import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    Output,
    signal,
} from '@angular/core';
import { SharedUiModule } from '@pf/shared-ui';
import { interval, Subscription } from 'rxjs';

@Component({
    selector: 'platform-auto-print-countdown',
    standalone: true,
    imports: [SharedUiModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (countdown() !== null) {
            <div class="auto-print-banner">
                <pf-text> Auto-printing in {{ countdown() }}s... </pf-text>
                <pf-button buttonType="cancel" (click)="cancel()">
                    Cancel
                </pf-button>
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: contents;
            }

            .auto-print-banner {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: var(--pf-gap);
                padding: var(--pf-gap);
                margin-bottom: var(--pf-gap);
                background: var(
                    --ant-info-color-deprecated-bg,
                    rgba(24, 144, 255, 0.1)
                );
                border-radius: 4px;
            }
        `,
    ],
})
export class AutoPrintCountdownComponent {
    @Input() seconds = 3;
    @Output() completed = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();

    readonly countdown = signal<number | null>(null);
    private subscription?: Subscription;

    constructor() {
        inject(DestroyRef).onDestroy(() => this.stopTimer());
    }

    start(): void {
        this.stopTimer();
        this.countdown.set(this.seconds);
        this.subscription = interval(1000).subscribe(() => {
            const next = (this.countdown() ?? 0) - 1;
            if (next <= 0) {
                this.stopTimer();
                this.countdown.set(null);
                this.completed.emit();
            } else {
                this.countdown.set(next);
            }
        });
    }

    cancel(): void {
        if (this.countdown() === null) {
            return;
        }
        this.stopTimer();
        this.countdown.set(null);
        this.cancelled.emit();
    }

    stop(): void {
        this.stopTimer();
        this.countdown.set(null);
    }

    isActive(): boolean {
        return this.countdown() !== null;
    }

    private stopTimer(): void {
        this.subscription?.unsubscribe();
        this.subscription = undefined;
    }
}
